import React, { useState, useEffect } from 'react';
import { Compass, Navigation, AlertTriangle } from 'lucide-react';

// کعبہ کے درست کوآرڈینیٹس (Coordinates)
const KAABA_LAT = 21.422487;
const KAABA_LON = 39.826206;

// صارف کی لوکیشن سے قبلہ کا زاویہ نکالنے کا فارمولہ
const calculateQibla = (lat, lon) => {
  const PI = Math.PI;
  const latK = KAABA_LAT * (PI / 180);
  const lonK = KAABA_LON * (PI / 180);
  const latU = lat * (PI / 180);
  const lonU = lon * (PI / 180);

  const y = Math.sin(lonK - lonU);
  const x = Math.cos(latU) * Math.tan(latK) - Math.sin(latU) * Math.cos(lonK - lonU);
  let bearing = Math.atan2(y, x) * (180 / PI);
  
  return (bearing + 360) % 360;
};

const QiblaCompass = () => {
  const [heading, setHeading] = useState(0);
  const [smoothHeading, setSmoothHeading] = useState(0);
  const [qiblaBearing, setQiblaBearing] = useState(0);
  const [locationError, setLocationError] = useState('');
  const [compassError, setCompassError] = useState('');
  const [hasPermission, setHasPermission] = useState(false);
  const [hasVibrated, setHasVibrated] = useState(false);

  // 1. لوکیشن لانا اور قبلہ کا زاویہ نکالنا
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const { latitude, longitude } = pos.coords;
          const bearing = calculateQibla(latitude, longitude);
          setQiblaBearing(Math.round(bearing));
        },
        (err) => {
          if (err.code === 1) setLocationError("لوکیشن کی اجازت نہیں ملی۔ سیٹنگز میں لوکیشن آن کریں۔");
          else setLocationError("آپ کا مقام تلاش کرنے میں مسئلہ ہوا۔");
        },
        { enableHighAccuracy: true }
      );
    } else {
      setLocationError("آپ کے آلے میں GPS موجود نہیں ہے۔");
    }
  }, []);

  // 2. کمپاس کی سوئی کو سموتھ (Smooth) گھمانا تاکہ 359 سے 0 پر جھٹکا نہ لگے
  useEffect(() => {
    setSmoothHeading((prev) => {
      let delta = heading - (prev % 360);
      if (delta > 180) delta -= 360;
      if (delta < -180) delta += 360;
      return prev + delta;
    });
  }, [heading]);

  // 3. سینسرز کی پرمیشن (خاص طور پر iPhone / iOS کے لیے)
  const requestAccess = async () => {
    if (typeof DeviceOrientationEvent !== 'undefined' && typeof DeviceOrientationEvent.requestPermission === 'function') {
      try {
        const permission = await DeviceOrientationEvent.requestPermission();
        if (permission === 'granted') {
          setHasPermission(true);
        } else {
          setCompassError("کمپاس کی اجازت نامنظور ہو گئی۔");
        }
      } catch (e) {
        console.error(e);
      }
    } else {
      setHasPermission(true);
    }
  };

  useEffect(() => {
    if (!(typeof DeviceOrientationEvent !== 'undefined' && typeof DeviceOrientationEvent.requestPermission === 'function')) {
      setHasPermission(true);
    }
  }, []);

  // 4. فون کی حرکت (Orientation) کو پڑھنا
  useEffect(() => {
    if (!hasPermission) return;

    const handler = (e) => {
      let compassHeading = null;
      if (e.webkitCompassHeading) {
        compassHeading = e.webkitCompassHeading;
      } else if (e.absolute && e.alpha !== null) {
        compassHeading = 360 - e.alpha;
      }

      if (compassHeading !== null) {
        setHeading(compassHeading);
        setCompassError('');
      } else {
        setCompassError("آپ کے آلے میں میگنیٹک سینسر نہیں ہے۔");
      }
    };

    if ('ondeviceorientationabsolute' in window) {
      window.addEventListener('deviceorientationabsolute', handler);
    } else {
      window.addEventListener('deviceorientation', handler);
    }

    return () => {
      window.removeEventListener('deviceorientationabsolute', handler);
      window.removeEventListener('deviceorientation', handler);
    };
  }, [hasPermission]);

  // 5. کیا فون کا رخ قبلہ کی طرف ہے؟ (Vibration Feedback)
  const normalizedHeading = ((smoothHeading % 360) + 360) % 360;
  const diff = Math.abs(normalizedHeading - qiblaBearing);
  const isFacingQibla = qiblaBearing > 0 && (diff < 3 || diff > 357); // 3 ڈگری کی گنجائش

  useEffect(() => {
    if (isFacingQibla && !hasVibrated) {
      if (navigator.vibrate) navigator.vibrate([50, 50, 50]); 
      setHasVibrated(true);
    } else if (!isFacingQibla && hasVibrated) {
      setHasVibrated(false);
    }
  }, [isFacingQibla, hasVibrated]);

  return (
    <>
      <style>{`
        /* 100% Theme Aware CSS Variables Support */
        .qibla-container {
          padding: 20px 15px 120px 15px; 
          display: flex; flex-direction: column; align-items: center; justify-content: flex-start;
          min-height: 100vh; animation: fadeIn 0.6s ease;
        }
        .qibla-header-box { text-align: center; margin-bottom: 35px; margin-top: 10px; }
        .qibla-title-urdu {
          font-family: 'Noto Nastaliq Urdu', serif; font-size: 1.8rem; color: var(--gold-primary); 
          font-weight: 700; text-shadow: 0 0 15px var(--gold-glow); margin: 0 0 8px 0;
          transition: color 0.4s ease, text-shadow 0.4s ease;
        }
        .qibla-subtitle {
          font-family: 'Noto Nastaliq Urdu', serif; font-size: 0.95rem; color: var(--text-muted); margin: 0;
          transition: color 0.4s ease;
        }
        
        /* Compass Dial Layout */
        .compass-master-wrapper {
          position: relative; width: 280px; height: 280px; margin: 10px auto 50px auto;
        }
        
        /* THEME FIX: Using var(--glass-border) instead of hardcoded RGBA */
        .compass-outer-ring {
          width: 100%; height: 100%; border-radius: 50%;
          background: var(--glass-bg); backdrop-filter: blur(20px); -webkit-backdrop-filter: blur(20px);
          border: 3px solid var(--glass-border);
          box-shadow: inset 0 0 30px var(--glass-shadow), 0 10px 40px var(--glass-shadow);
          position: relative; transition: transform 0.1s linear, box-shadow 0.4s ease, border-color 0.4s ease, background 0.4s ease;
        }
        .compass-outer-ring.found-qibla {
          border-color: var(--gold-shiny);
          box-shadow: inset 0 0 40px var(--gold-glow), 0 0 50px var(--gold-glow-strong);
        }
        .compass-center-pin {
          position: absolute; width: 14px; height: 14px; background: var(--gold-shiny);
          border-radius: 50%; top: 50%; left: 50%; transform: translate(-50%, -50%);
          box-shadow: 0 0 15px var(--gold-glow-strong); z-index: 20;
          transition: background 0.4s ease, box-shadow 0.4s ease;
        }
        
        /* N, E, S, W Markers */
        .compass-marker {
          position: absolute; font-family: 'Cinzel', serif; font-weight: 800; font-size: 1.2rem;
          color: var(--text-muted); opacity: 0.8; transition: color 0.4s ease;
        }
        .compass-marker.north { top: 12px; left: 50%; transform: translateX(-50%); color: #ff4d4d; opacity: 1;}
        .compass-marker.south { bottom: 12px; left: 50%; transform: translateX(-50%); }
        .compass-marker.east  { right: 18px; top: 50%; transform: translateY(-50%); }
        .compass-marker.west  { left: 18px; top: 50%; transform: translateY(-50%); }
        
        /* Kaaba Pointer (Inside Dial) */
        .kaaba-indicator-layer {
          position: absolute; top: 0; left: 0; width: 100%; height: 100%;
          transition: transform 0.3s ease-out; z-index: 5;
        }
        .kaaba-beam {
          position: absolute; top: 50%; left: 50%; width: 2px; height: 40%;
          background: linear-gradient(to top, transparent, var(--gold-primary));
          transform-origin: bottom center; transform: translate(-50%, -100%);
        }
        .kaaba-icon-3d {
          position: absolute; top: 4%; left: 50%; transform: translateX(-50%);
          font-size: 2rem; filter: drop-shadow(0 0 15px var(--gold-glow));
        }
        
        /* Phone Pointer (Top Fixed Arrow) */
        .phone-pointer-arrow {
          position: absolute; top: -22px; left: 50%; transform: translateX(-50%);
          width: 0; height: 0; border-left: 15px solid transparent; border-right: 15px solid transparent;
          border-bottom: 25px solid var(--text-primary); z-index: 30;
          transition: border-bottom-color 0.4s ease, filter 0.4s ease;
        }
        .found-qibla ~ .phone-pointer-arrow {
          border-bottom-color: var(--gold-shiny); filter: drop-shadow(0 -5px 15px var(--gold-glow-strong));
        }

        /* Stats Cards */
        .qibla-data-cards {
          display: grid; grid-template-columns: 1fr 1fr; gap: 15px; width: 100%; max-width: 360px;
        }
        .q-data-card {
          background: var(--card-gradient); border: 1px solid var(--glass-border);
          border-radius: 16px; padding: 15px 10px; display: flex; flex-direction: column; align-items: center; justify-content: center;
          box-shadow: 0 5px 20px var(--glass-shadow); text-align: center; position: relative; overflow: hidden;
          transition: background 0.4s ease, border-color 0.4s ease, box-shadow 0.4s ease;
        }
        .q-data-card.active-glow {
          border-color: var(--gold-primary); box-shadow: 0 0 20px var(--gold-glow);
        }
        .q-data-card .icon-gold { color: var(--gold-primary); margin-bottom: 8px; transition: color 0.4s ease; }
        .q-data-card strong { font-family: 'Poppins', sans-serif; font-size: 1.4rem; color: var(--text-primary); font-weight: 700; line-height: 1; transition: color 0.4s ease; }
        .q-data-card small { font-family: 'Noto Nastaliq Urdu', serif; font-size: 0.85rem; color: var(--text-muted); margin-top: 5px; transition: color 0.4s ease; }
        
        .qibla-alert {
          margin-top: 20px; background: rgba(255, 77, 77, 0.1); border: 1px dashed #ff4d4d;
          color: #ff4d4d; padding: 10px 15px; border-radius: 12px; font-family: 'Noto Nastaliq Urdu', serif; font-size: 0.85rem;
          display: flex; align-items: center; gap: 8px; text-align: center;
        }
        
        .permission-btn-qibla {
          background: linear-gradient(135deg, var(--gold-primary), var(--gold-dim)); border: none; color: #fff;
          padding: 12px 25px; border-radius: 12px; font-family: 'Noto Nastaliq Urdu', serif; font-size: 1.1rem;
          margin-top: 30px; cursor: pointer; display: flex; align-items: center; gap: 10px; box-shadow: 0 5px 15px var(--gold-glow);
          transition: background 0.4s ease, box-shadow 0.4s ease;
        }
      `}</style>

      <div className="qibla-container">
        
        {/* ہیڈر سیکشن */}
        <div className="qibla-header-box">
          <h2 className="qibla-title-urdu">قبلہ رخ</h2>
          <p className="qibla-subtitle">
            {isFacingQibla ? (
              <span style={{color: 'var(--gold-shiny)', fontWeight: 'bold', textShadow: '0 0 10px var(--gold-glow)'}}>الحمدللہ! آپ کا رخ قبلہ کی طرف ہے۔</span>
            ) : (
              "درست سمت جاننے کے لیے فون کو زمین کے متوازی (سیدھا) رکھیں۔"
            )}
          </p>
        </div>

        {/* پرمیشن بٹن برائے iOS */}
        {!hasPermission && typeof DeviceOrientationEvent !== 'undefined' && typeof DeviceOrientationEvent.requestPermission === 'function' && (
          <button onClick={requestAccess} className="permission-btn-qibla">
            <Compass size={20} /> سینسر کی اجازت دیں
          </button>
        )}

        {/* مین کمپاس ڈائل */}
        <div className="compass-master-wrapper" style={{ opacity: hasPermission ? 1 : 0.3 }}>
          <div 
            className={`compass-outer-ring ${isFacingQibla ? 'found-qibla' : ''}`} 
            style={{ transform: `rotate(${-smoothHeading}deg)` }}
          >
            <div className="compass-marker north">N</div>
            <div className="compass-marker east">E</div>
            <div className="compass-marker south">S</div>
            <div className="compass-marker west">W</div>
            
            <div className="kaaba-indicator-layer" style={{ transform: `rotate(${qiblaBearing}deg)` }}>
                <div className="kaaba-beam"></div>
                <div className="kaaba-icon-3d">🕋</div>
            </div>
            
            <div className="compass-center-pin"></div>
          </div>
          
          <div className="phone-pointer-arrow"></div>
        </div>

        {/* نیویگیشن ڈیٹا کارڈز */}
        <div className="qibla-data-cards">
          <div className="q-data-card">
              <Navigation size={22} className="icon-gold" />
              <strong>{qiblaBearing ? `${qiblaBearing}°` : '--°'}</strong>
              <small>زاویہ قبلہ</small>
          </div>
          <div className={`q-data-card ${isFacingQibla ? 'active-glow' : ''}`}>
              <Compass size={22} className="icon-gold" />
              <strong>{Math.round(normalizedHeading)}°</strong>
              <small>موجودہ رخ</small>
          </div>
        </div>

        {/* ایررز (اگر GPS یا سینسر بند ہوں) */}
        {(locationError || compassError) && (
          <div className="qibla-alert">
             <AlertTriangle size={18} />
             <span>{locationError || compassError}</span>
          </div>
        )}

      </div>
    </>
  );
};

export default QiblaCompass;